import {Button, ToggleButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Typeahead} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {PersonLine, SearchLine, WarningLine} from '@dropbox/dig-icons/assets';
import {ApiError} from 'client/core/ApiError';
import type {RoadmapAuditAssigneeFacet} from 'client/models/RoadmapAuditAssigneeFacet';
import type {RoadmapAuditFindingsResponse} from 'client/models/RoadmapAuditFindingsResponse';
import {EmptyState} from 'components/DSYS/EmptyState';
import {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {AuditFindingsLoadingSkeleton} from 'views/roadmap/audit/AuditFindingsLoadingSkeleton';
import {AuditFindingsTable} from 'views/roadmap/audit/AuditFindingsTable';
import {DEFAULT_ROADMAP_AUDIT_FILTERS, type RoadmapAuditFilters} from 'views/roadmap/queryParams';
import styles from 'views/roadmap/Roadmap.module.css';

export interface AuditFindingsTabProps {
  state: 'idle' | 'loading' | 'error' | 'data';
  data: RoadmapAuditFindingsResponse | undefined;
  error: Error | null;
  filters: RoadmapAuditFilters;
  expansionKey: string;
  isLoadingMore: boolean;
  nextCursor: string | null;
  onFiltersChange: (filters: RoadmapAuditFilters) => void;
  onLoadMore: (cursor: string) => void;
  onResetPagination: () => void;
  onRetry: () => void;
}

const getAuditErrorCode = (error: Error | null) => {
  const cause = error?.cause;
  return cause instanceof ApiError && typeof cause.body?.detail?.code === 'string'
    ? cause.body.detail.code
    : null;
};

const AuditAssigneeTypeahead = ({
  assignees,
  selectedKeys,
  onSelect,
}: {
  assignees: RoadmapAuditAssigneeFacet[];
  selectedKeys: string[];
  onSelect: (key: string) => void;
}) => {
  const [query, setQuery] = useState('');
  const inputContainerRef = useRef<HTMLElement | null>(null);
  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const options = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return assignees
      .filter((assignee) => !selectedKeySet.has(assignee.key))
      .filter(
        (assignee) =>
          !normalizedQuery || assignee.display_name.toLocaleLowerCase().includes(normalizedQuery)
      );
  }, [assignees, query, selectedKeySet]);

  const selectAssignee = (key: string) => {
    onSelect(key);
    setQuery('');
  };

  return (
    <Typeahead.Wrapper isPortaled={false} onSelection={(key) => selectAssignee(String(key))}>
      {({getContentProps, getTriggerProps}) => (
        <div
          className={styles.auditFindingsPeopleSearch}
          data-testid="audit-findings-people-search"
        >
          <TextInput.Container ref={inputContainerRef} size="small">
            <TextInput.Accessory>
              <UIIcon src={SearchLine} size="small" />
            </TextInput.Accessory>
            <TextInput.Input
              aria-label="Search people"
              placeholder="Search people…"
              autoComplete="off"
              value={query}
              {...getTriggerProps({
                onChange: (event) => setQuery((event.currentTarget as HTMLInputElement).value),
              })}
            />
          </TextInput.Container>
          <Typeahead.Container
            {...getContentProps()}
            triggerRef={inputContainerRef}
            className={styles.auditFindingsPeopleResults}
          >
            {options.length > 0 ? (
              <Typeahead.Results
                initialResults={8}
                maxResults={8}
                results={options}
                renderRow={(assignee) => (
                  <Typeahead.Row
                    key={assignee.key}
                    value={assignee.key}
                    withTitle={<Text>{assignee.display_name}</Text>}
                    withSubtitle={
                      <Text size="small" color="subtle">
                        {assignee.count} flagged
                      </Text>
                    }
                  />
                )}
              />
            ) : query ? (
              <Typeahead.Row disabled value="no-results" withTitle={<Text>No people found</Text>} />
            ) : null}
          </Typeahead.Container>
        </div>
      )}
    </Typeahead.Wrapper>
  );
};

export const AuditFindingsTab = ({
  state,
  data,
  error,
  filters,
  expansionKey,
  isLoadingMore,
  nextCursor,
  onFiltersChange,
  onLoadMore,
  onResetPagination,
  onRetry,
}: AuditFindingsTabProps) => {
  const [notice, setNotice] = useState<string | null>(null);
  const [lastContentHeight, setLastContentHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const lastDataRef = useRef<RoadmapAuditFindingsResponse | undefined>(data);
  if (data) lastDataRef.current = data;
  const displayedData = data ?? lastDataRef.current;
  const isTableLoading = state === 'loading' && Boolean(displayedData);
  const rules = displayedData?.facets.rules ?? [];
  const assignees = useMemo(() => displayedData?.facets.assignees ?? [], [displayedData]);
  const assigneesByKey = useMemo(
    () => new Map(assignees.map((assignee) => [assignee.key, assignee])),
    [assignees]
  );
  const errorCode = getAuditErrorCode(error);

  useLayoutEffect(() => {
    if (state !== 'data' || !contentRef.current) return;
    const bounds = contentRef.current.getBoundingClientRect();
    const height = Math.ceil(bounds?.height ?? contentRef.current.scrollHeight);
    if (height > 0) setLastContentHeight(height);
  }, [data, state]);

  useEffect(() => {
    if (filters.ruleIds.length === 0) return;

    const knownRuleIds = data ? new Set((data.facets.rules ?? []).map((rule) => rule.id)) : null;
    const hasUnknownRuleFromCatalog =
      knownRuleIds !== null && filters.ruleIds.some((ruleId) => !knownRuleIds.has(ruleId));
    const hasUnknownRuleError = getAuditErrorCode(error) === 'unknown_audit_rule';
    if (!hasUnknownRuleFromCatalog && !hasUnknownRuleError) return;

    onFiltersChange({...filters, ruleIds: [], ruleMode: 'any'});
    setNotice('A retired audit rule was removed from this view.');
  }, [data, error, filters, onFiltersChange]);

  const updateRules = (ruleIds: string[]) => {
    setNotice(null);
    onFiltersChange({...filters, ruleIds, ruleMode: 'any'});
  };

  const toggleRule = (ruleId: string) => {
    const selected = filters.ruleIds.includes(ruleId);
    updateRules(
      selected
        ? filters.ruleIds.filter((selectedRuleId) => selectedRuleId !== ruleId)
        : [...filters.ruleIds, ruleId]
    );
  };

  const toggleMine = () => {
    onFiltersChange({
      ...filters,
      assignee: filters.assignee === 'me' ? 'all' : 'me',
      assigneeKeys: [],
    });
  };

  const selectAssignee = (key: string) => {
    if (filters.assigneeKeys.includes(key)) return;
    onFiltersChange({
      ...filters,
      assignee: 'all',
      assigneeKeys: [...filters.assigneeKeys, key],
    });
  };

  const removeAssignee = (key: string) => {
    onFiltersChange({
      ...filters,
      assignee: 'all',
      assigneeKeys: filters.assigneeKeys.filter((selectedKey) => selectedKey !== key),
    });
  };

  const showAllInitiatives = () => onFiltersChange({...filters, assignee: 'all', assigneeKeys: []});

  const hasActiveFilters =
    filters.assignee !== 'all' ||
    filters.assigneeKeys.length > 0 ||
    filters.ruleIds.length > 0 ||
    Boolean(filters.query);
  const clearFilters = () => {
    setNotice(null);
    onFiltersChange({...DEFAULT_ROADMAP_AUDIT_FILTERS});
  };

  return (
    <section
      className={`${styles.section} ${styles.auditFindingsSection}`}
      data-testid="roadmap-audit-findings-tab"
      data-state={state}
      aria-busy={state === 'loading'}
    >
      {state === 'loading' && !displayedData ? (
        <AuditFindingsLoadingSkeleton minimumHeight={lastContentHeight || undefined} />
      ) : null}
      {state === 'error' && errorCode !== 'unknown_audit_rule' ? (
        <Box
          padding="24"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          role="alert"
          style={{gap: 12, minHeight: lastContentHeight || 320}}
        >
          <Text isBold>
            {errorCode === 'roadmap_audit_identity_unavailable'
              ? 'We could not match your Jira identity.'
              : errorCode === 'audit_cursor_generation_mismatch'
                ? 'The Roadmap refreshed while more findings were loading.'
                : 'Unable to load audit findings.'}
          </Text>
          <Text color="subtle">
            {errorCode === 'roadmap_audit_identity_unavailable'
              ? 'You can still review all initiatives or select a person.'
              : errorCode === 'audit_cursor_generation_mismatch'
                ? 'Reload the first page to use the newest Roadmap snapshot.'
                : error?.message}
          </Text>
          <Button
            variant="outline"
            size="small"
            onClick={
              errorCode === 'roadmap_audit_identity_unavailable'
                ? showAllInitiatives
                : errorCode === 'audit_cursor_generation_mismatch'
                  ? onResetPagination
                  : onRetry
            }
          >
            {errorCode === 'roadmap_audit_identity_unavailable'
              ? 'Show all initiatives'
              : errorCode === 'audit_cursor_generation_mismatch'
                ? 'Reload results'
                : 'Retry'}
          </Button>
        </Box>
      ) : null}
      {state === 'idle' ? (
        <Box padding="24">
          <Text color="subtle">Select a Roadmap timeframe and quarter to load audit findings.</Text>
        </Box>
      ) : null}
      {(state === 'data' || isTableLoading) && displayedData ? (
        <div
          ref={contentRef}
          className={styles.auditFindingsContent}
          data-testid="roadmap-audit-findings-data"
          style={isTableLoading && lastContentHeight ? {minHeight: lastContentHeight} : undefined}
        >
          <div className={styles.auditFindingsOwnershipControls}>
            <AuditAssigneeTypeahead
              assignees={assignees}
              selectedKeys={filters.assigneeKeys}
              onSelect={selectAssignee}
            />
            <ToggleButton
              variant="outline"
              size="small"
              iconSrc={PersonLine}
              withTextEnd="My initiatives"
              value="mine"
              aria-label="My initiatives"
              isSelected={filters.assignee === 'me'}
              onSelection={() => toggleMine()}
            />
          </div>

          {filters.assigneeKeys.length > 0 ? (
            <div className={styles.auditFindingsSelectedPeople} aria-label="Selected people">
              {filters.assigneeKeys.map((key) => {
                const assignee = assigneesByKey.get(key);
                const label = assignee?.display_name || 'Unresolved assignee';
                return (
                  <Chip
                    key={key}
                    size="small"
                    aria-label={`Remove ${label}`}
                    onDelete={() => removeAssignee(key)}
                  >
                    <Chip.Content>{label}</Chip.Content>
                  </Chip>
                );
              })}
            </div>
          ) : null}

          <div
            className={styles.auditFindingsRuleFilters}
            role="group"
            aria-label="Audit rule filters"
          >
            <Chip
              size="small"
              isSelected={filters.ruleIds.length === 0}
              aria-pressed={filters.ruleIds.length === 0}
              onClick={() => updateRules([])}
            >
              <Chip.Content>All {displayedData.summary.flagged_initiative_count}</Chip.Content>
            </Chip>
            {rules.map((rule) => (
              <Chip
                key={rule.id}
                size="small"
                isSelected={filters.ruleIds.includes(rule.id)}
                aria-pressed={filters.ruleIds.includes(rule.id)}
                title={rule.description}
                onClick={() => toggleRule(rule.id)}
              >
                <Chip.Content>
                  {rule.label} {rule.count}
                </Chip.Content>
              </Chip>
            ))}
          </div>

          {displayedData.meta.freshness.is_stale ? (
            <SystemBanner tone="warning" role="status" data-testid="audit-findings-stale-warning">
              <Text>
                <UIIcon src={WarningLine} size="small" /> Audit results may be out of date. Last
                successful snapshot:{' '}
                {new Date(displayedData.meta.freshness.snapshot_built_at).toLocaleString()}.
              </Text>
            </SystemBanner>
          ) : null}
          <span className={styles.auditFindingsLiveRegion} aria-live="polite" aria-atomic="true">
            {displayedData.summary.flagged_initiative_count} flagged initiatives
          </span>
          {notice ? (
            <Text size="small" color="subtle" role="status">
              {notice}
            </Text>
          ) : null}
          {!isTableLoading && displayedData.items.length === 0 ? (
            <div data-testid="audit-findings-empty-state">
              <EmptyState
                hideBorder
                image={null}
                marginBottom="0"
                paddingY="44"
                title={hasActiveFilters ? 'No matching initiatives' : 'No flagged initiatives'}
                body={
                  hasActiveFilters
                    ? 'Clear your filters or select another quarter.'
                    : 'Try selecting another quarter.'
                }
                cta={
                  hasActiveFilters
                    ? {
                        text: 'Clear filters',
                        onClick: clearFilters,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <AuditFindingsTable
              items={displayedData.items}
              expansionKey={expansionKey}
              isLoading={isTableLoading}
              nextCursor={nextCursor}
              isLoadingMore={isLoadingMore}
              onLoadMore={onLoadMore}
            />
          )}
        </div>
      ) : null}
    </section>
  );
};
